/**
 * THE single frontend definition of key-result and OKR progress.
 *
 * This is a deliberate MIRROR of `backend/src/utils/progress.ts`. The two must agree
 * value-for-value: the backend number reaches the UI through the API on some screens and
 * is recomputed locally on others, so any drift shows up as the same OKR reporting two
 * different percentages in the same session.
 *
 * Before 2026-08-11 this arithmetic was written out twelve times across the repo — nine
 * copies here, three in backend services — in four incompatible variants. Seven of the
 * copies below omitted the clamp; one (Dashboard) alone handled a met zero-range target,
 * and did so with a `>=` that scored three incidents as 100% of a zero-incidents goal.
 *
 * If you change a rule here, change it in the backend file in the same commit.
 */

export interface KeyResultForProgress {
  startValue: number;
  targetValue: number;
  currentValue: number;
}

/**
 * Progress for one key result, unrounded and clamped to 0..100.
 *
 * Rules, each of which some implementation got wrong before this module existed:
 *
 * 1. A DECREASING key result counts. "Reduce defect rate from 5% to 2%" has a negative
 *    range; dividing a negative delta by it yields correct positive progress.
 * 2. A ZERO-RANGE key result reads 100 only when it sits EXACTLY on target. "Achieve Zero
 *    Lost-Time Incidents" at zero is achieved; at three it is not. A zero range carries no
 *    sign convention, so `>=` cannot be used to decide it.
 * 3. The result is CLAMPED, so an overshooting key result cannot render past 100% and a
 *    regressing one cannot render below 0%.
 *
 * Prefer `keyResultProgressPercent` for anything user-facing — this returns the raw value
 * so that an OKR mean rounds once rather than once per key result.
 */
export function calculateKeyResultProgress(kr: KeyResultForProgress): number {
  const range = kr.targetValue - kr.startValue;

  if (range === 0) return kr.currentValue === kr.targetValue ? 100 : 0;

  const progress = ((kr.currentValue - kr.startValue) / range) * 100;

  return Math.min(100, Math.max(0, progress));
}

/**
 * Progress for one key result as a whole number, for display: bar widths, badges, labels.
 */
export function keyResultProgressPercent(kr: KeyResultForProgress): number {
  return Math.round(calculateKeyResultProgress(kr));
}

/**
 * Progress for an OKR: the unweighted mean of its key results, rounded once at the end.
 *
 * An objective with no key results of its own reports 0 and does NOT inherit its children's
 * progress — deliberate, and matching the backend. Tree roll-up is a separate feature.
 */
export function calculateOKRProgress(keyResults: KeyResultForProgress[]): number {
  if (!keyResults || keyResults.length === 0) return 0;

  const totalProgress = keyResults.reduce(
    (sum, kr) => sum + calculateKeyResultProgress(kr),
    0
  );

  return Math.round(totalProgress / keyResults.length);
}
